import { ImportCandidateDto, ImportPreviewRowDto, ImportRowAction } from '../../../api/created/importFile.ts';

// A row marked as not imported (SKIP) is simply not submitted.
export type RowDecisionAction = ImportRowAction | 'SKIP';

export interface RowDecision {
  action: RowDecisionAction;
  // The person to link, only used by the two link actions.
  personId: string | null;
}

export type RowDecisions = Record<number, RowDecision>;

// Option A links the Stammdaten person and keeps its data, Option B links it but takes the Excel data.
export const ROW_DECISION_LABELS: Record<RowDecisionAction, string> = {
  USE_EXISTING: 'Stammdaten übernehmen',
  USE_EXISTING_WITH_FILE_DATA: 'Excel-Daten übernehmen',
  CREATE_NEW: 'Neue Person erstellen',
  SKIP: 'Nicht importieren',
};

export function isLinkAction(action: RowDecisionAction): action is 'USE_EXISTING' | 'USE_EXISTING_WITH_FILE_DATA' {
  return action === 'USE_EXISTING' || action === 'USE_EXISTING_WITH_FILE_DATA';
}

// All candidates can be linked, `alreadyAssigned` is intentionally not taken into account.
export function getLinkableCandidates(row: ImportPreviewRowDto): ImportCandidateDto[] {
  return row.duplicate.candidates;
}

// Every row can be left out, rows with errors can only be left out. A full match links the
// Stammdaten person (Option A or B), any other row creates a new person.
export function getAvailableActions(row: ImportPreviewRowDto): RowDecisionAction[] {
  if (row.errors.length > 0) {
    return ['SKIP'];
  }
  return row.duplicate.matchLevel === 'FULL'
    ? ['USE_EXISTING', 'USE_EXISTING_WITH_FILE_DATA', 'SKIP']
    : ['CREATE_NEW', 'SKIP'];
}

// The preview's suggestion. Without one the row starts as not imported.
export function getSuggestedDecision(row: ImportPreviewRowDto): RowDecision {
  const { suggestedAction } = row.duplicate;
  if (row.errors.length > 0 || !suggestedAction) {
    return { action: 'SKIP', personId: null };
  }
  return withDefaultPerson(row, { action: suggestedAction, personId: null });
}

// A link action always has a person: the preview's suggestion or, when several persons match
// fully and the preview leaves the choice open, the best match. The candidates come sorted with
// the full matches first, and the user can still pick another one.
export function withDefaultPerson(row: ImportPreviewRowDto, decision: RowDecision): RowDecision {
  if (!isLinkAction(decision.action) || decision.personId) {
    return decision;
  }
  const candidates = getLinkableCandidates(row);
  const personId =
    row.duplicate.suggestedPersonId ??
    (candidates.find((candidate) => candidate.fullMatch) ?? candidates[0])?.person.id ??
    null;
  return { ...decision, personId };
}
